import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, CheckCircle2, XCircle, BookOpen, Target,
  Trophy, Clock, ChevronRight, Sparkles, Brain, Calculator,
  BookA, Shapes, Zap, Star, Play, ArrowRight, Lightbulb,
  HelpCircle, Award, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subject config
const SUBJECT_CONFIG = {
  maths: { icon: Calculator, color: 'from-blue-600 to-indigo-600', bg: 'bg-blue-500/20', text: 'text-blue-600', name: 'Mathematics' },
  english: { icon: BookA, color: 'from-green-600 to-emerald-600', bg: 'bg-green-500/20', text: 'text-green-600', name: 'English' },
  verbal_reasoning: { icon: Brain, color: 'from-purple-600 to-pink-600', bg: 'bg-purple-500/20', text: 'text-purple-600', name: 'Verbal Reasoning' },
  non_verbal_reasoning: { icon: Shapes, color: 'from-orange-600 to-red-600', bg: 'bg-orange-500/20', text: 'text-orange-600', name: 'Non-Verbal Reasoning' },
  mixed: { icon: Target, color: 'from-violet-600 to-indigo-600', bg: 'bg-violet-500/20', text: 'text-violet-600', name: 'Mixed Topics' },
  all: { icon: Sparkles, color: 'from-amber-600 to-orange-600', bg: 'bg-amber-500/20', text: 'text-amber-600', name: 'All Subjects' }
};

// Sample module content (in production, this would come from the API)
const generateModuleContent = (subject, type) => {
  const content = {
    title: `${subject.replace(/_/g, ' ')} ${type === 'learn' ? 'Lesson' : type === 'practice' ? 'Practice' : 'Challenge'}`,
    sections: [
      {
        type: 'intro',
        title: 'Introduction',
        content: `Welcome to this ${type} session on ${subject.replace(/_/g, ' ')}. Let's strengthen your skills!`
      },
      {
        type: 'concept',
        title: 'Key Concepts',
        content: 'In this module, you will learn important concepts that will help you master this topic.',
        points: [
          'Understanding the fundamentals',
          'Applying problem-solving strategies',
          'Recognizing common patterns'
        ]
      },
      {
        type: 'example',
        title: 'Worked Example',
        content: 'Let\'s work through an example together to see how these concepts apply.'
      }
    ],
    questions: generateQuizQuestions(subject)
  };
  return content;
};

// Generate 3 quiz questions for module completion
const generateQuizQuestions = (subject) => {
  const questionSets = {
    maths: [
      {
        question: 'What is 25% of 80?',
        options: ['15', '20', '25', '30'],
        correct: 1,
        explanation: '25% = 1/4, so 80 ÷ 4 = 20'
      },
      {
        question: 'Simplify: 3x + 2x - x',
        options: ['4x', '5x', '6x', '3x'],
        correct: 0,
        explanation: '3x + 2x - x = (3 + 2 - 1)x = 4x'
      },
      {
        question: 'What is the area of a rectangle with length 8cm and width 5cm?',
        options: ['26 cm²', '40 cm²', '13 cm²', '45 cm²'],
        correct: 1,
        explanation: 'Area = length × width = 8 × 5 = 40 cm²'
      }
    ],
    english: [
      {
        question: 'Which word is a synonym for "happy"?',
        options: ['Sad', 'Joyful', 'Angry', 'Tired'],
        correct: 1,
        explanation: '"Joyful" means full of joy, which is similar to being happy.'
      },
      {
        question: 'Identify the noun in: "The cat jumped over the fence."',
        options: ['jumped', 'over', 'cat', 'the'],
        correct: 2,
        explanation: '"Cat" and "fence" are nouns. "Cat" is the subject of the sentence.'
      },
      {
        question: 'What is the past tense of "run"?',
        options: ['Runned', 'Running', 'Ran', 'Runs'],
        correct: 2,
        explanation: 'The past tense of "run" is "ran" (irregular verb).'
      }
    ],
    verbal_reasoning: [
      {
        question: 'Complete the analogy: Book is to Reading as Fork is to ___',
        options: ['Writing', 'Eating', 'Cooking', 'Cleaning'],
        correct: 1,
        explanation: 'A book is used for reading, just as a fork is used for eating.'
      },
      {
        question: 'Which word does not belong: Apple, Banana, Carrot, Orange?',
        options: ['Apple', 'Banana', 'Carrot', 'Orange'],
        correct: 2,
        explanation: 'Carrot is a vegetable; the others are fruits.'
      },
      {
        question: 'If APPLE = 50, what is PEAR?',
        options: ['40', '44', '48', '52'],
        correct: 1,
        explanation: 'Each letter value: A=1, P=16, P=16, L=12, E=5 = 50. P=16, E=5, A=1, R=18 = 40... (This is a simplified example)'
      }
    ],
    non_verbal_reasoning: [
      {
        question: 'If a shape is rotated 90° clockwise, which position will it be in?',
        options: ['Same position', 'Turned right', 'Upside down', 'Turned left'],
        correct: 1,
        explanation: 'A 90° clockwise rotation turns the shape to the right.'
      },
      {
        question: 'In a sequence of shapes, if the pattern adds one side each time (triangle, square, pentagon), what comes next?',
        options: ['Circle', 'Hexagon', 'Rectangle', 'Octagon'],
        correct: 1,
        explanation: 'Triangle has 3 sides, square has 4, pentagon has 5, so hexagon has 6 sides.'
      },
      {
        question: 'Which shape is the reflection of a right-pointing arrow in a vertical mirror?',
        options: ['Up-pointing arrow', 'Down-pointing arrow', 'Left-pointing arrow', 'Right-pointing arrow'],
        correct: 2,
        explanation: 'A vertical mirror flips left and right, so a right arrow becomes a left arrow.'
      }
    ]
  };

  return questionSets[subject] || questionSets.maths;
};

const LearningModule = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const { weekNumber, moduleIndex } = useParams();
  const location = useLocation();
  
  const weekNum = parseInt(weekNumber);
  const modIndex = parseInt(moduleIndex);
  
  // Get module data from location state or progress
  const moduleData = location.state?.module || progress?.learningPath?.weeks?.[weekNum - 1]?.modules?.[modIndex];
  
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [score, setScore] = useState(0);

  // Generate module content based on subject
  const moduleContent = generateModuleContent(
    moduleData?.subject || 'maths',
    moduleData?.type || 'learn'
  );

  const subjectConfig = SUBJECT_CONFIG[moduleData?.subject] || SUBJECT_CONFIG.maths;
  const SubjectIcon = subjectConfig.icon;

  // Handle moving to next section
  const handleNextSection = () => {
    if (currentSection < moduleContent.sections.length - 1) {
      setCurrentSection(prev => prev + 1);
    } else {
      // Start quiz after content
      setShowQuiz(true);
    }
  };

  // Handle answer selection
  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  // Handle answer submission
  const handleSubmitAnswer = () => {
    const isCorrect = selectedAnswer === moduleContent.questions[currentQuestion].correct;
    setAnswers(prev => [...prev, { selected: selectedAnswer, correct: isCorrect }]);
    setShowResult(true);
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
  };

  // Handle next question
  const handleNextQuestion = () => {
    if (currentQuestion < moduleContent.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      // Quiz complete
      setQuizComplete(true);
    }
  };

  // Handle module completion - now with backend API
  const handleCompleteModule = async () => {
    const token = localStorage.getItem('token');
    
    // Call backend API to record completion
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/learning-path/module/complete`, {
        week_number: weekNum,
        module_index: modIndex,
        quiz_score: score,
        quiz_total: 3
      }, { headers });
    } catch (err) {
      console.error('Failed to save module completion to backend:', err);
      // Continue with local update even if API fails
    }

    // Update progress locally
    if (progress?.learningPath?.weeks) {
      const updatedWeeks = progress.learningPath.weeks.map((week, wIndex) => {
        if (wIndex === weekNum - 1) {
          const updatedModules = week.modules.map((mod, mIndex) => {
            if (mIndex === modIndex) {
              return { ...mod, completed: true };
            }
            return mod;
          });
          
          // Calculate new week progress
          const completedModules = updatedModules.filter(m => m.completed).length;
          const weekProgress = Math.round((completedModules / updatedModules.length) * 100);
          
          // Check if all non-mock modules are complete
          const dailyModulesComplete = updatedModules
            .filter(m => m.type !== 'mock')
            .every(m => m.completed);
          
          return {
            ...week,
            modules: updatedModules,
            progress: weekProgress,
            mockUnlocked: dailyModulesComplete,
            status: weekProgress === 100 ? 'completed' : 'in_progress'
          };
        }
        
        // Unlock next week if current week is completed
        if (wIndex === weekNum) {
          const prevWeek = progress.learningPath.weeks[weekNum - 1];
          const prevWeekModules = prevWeek.modules.map((mod, mIndex) => 
            mIndex === modIndex ? { ...mod, completed: true } : mod
          );
          const prevWeekComplete = prevWeekModules.every(m => m.completed);
          
          if (prevWeekComplete && week.status === 'locked') {
            return { ...week, status: 'available' };
          }
        }
        
        return week;
      });

      updateProgress({
        learningPath: {
          ...progress.learningPath,
          weeks: updatedWeeks,
          lastUpdated: new Date().toISOString()
        }
      });
    }

    toast.success('Module completed! Great work!');
    navigate(`/learning-path/week/${weekNum}`);
  };

  if (!moduleData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <Card className="bg-white/50 border-slate-200 p-8 text-center">
          <HelpCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Module Not Found</h2>
          <p className="text-slate-500 mb-4">This module doesn't exist or you haven't navigated here correctly.</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div 
      data-testid="learning-module"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white"
    >
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className={`bg-gradient-to-r ${subjectConfig.color} py-6`}>
        <div className="max-w-3xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-100">
              <SubjectIcon className="w-8 h-8 text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">
                {moduleData.title}
              </h1>
              <p className="text-slate-600 text-sm">
                {moduleData.day} • Week {weekNum}
              </p>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-600">
                {showQuiz ? 'Quiz Progress' : 'Lesson Progress'}
              </span>
              <span className="text-slate-900 font-medium">
                {showQuiz 
                  ? `${currentQuestion + 1}/${moduleContent.questions.length}`
                  : `${currentSection + 1}/${moduleContent.sections.length}`
                }
              </span>
            </div>
            <Progress 
              value={showQuiz 
                ? ((currentQuestion + 1) / moduleContent.questions.length) * 100
                : ((currentSection + 1) / moduleContent.sections.length) * 100
              } 
              className="h-2 bg-slate-100" 
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {!showQuiz ? (
            // Lesson Content
            <motion.div
              key={`section-${currentSection}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white/50 border-slate-200">
                <CardHeader>
                  <Badge className={`${subjectConfig.bg} ${subjectConfig.text} w-fit mb-2`}>
                    Section {currentSection + 1}
                  </Badge>
                  <CardTitle className="text-slate-900 text-xl">
                    {moduleContent.sections[currentSection].title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600 leading-relaxed">
                    {moduleContent.sections[currentSection].content}
                  </p>
                  
                  {moduleContent.sections[currentSection].points && (
                    <ul className="space-y-2">
                      {moduleContent.sections[currentSection].points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                          <span className="text-slate-600">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="pt-4">
                    <Button
                      data-testid="next-section-btn"
                      onClick={handleNextSection}
                      className={`bg-gradient-to-r ${subjectConfig.color}`}
                    >
                      {currentSection < moduleContent.sections.length - 1 
                        ? 'Next Section' 
                        : 'Start Quiz'
                      }
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : !quizComplete ? (
            // Quiz Questions
            <motion.div
              key={`question-${currentQuestion}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white/50 border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-amber-500/20 text-amber-600">
                      Question {currentQuestion + 1} of 3
                    </Badge>
                    <Badge className={`${subjectConfig.bg} ${subjectConfig.text}`}>
                      {subjectConfig.name}
                    </Badge>
                  </div>
                  <CardTitle className="text-slate-900 text-lg mt-4">
                    {moduleContent.questions[currentQuestion].question}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {moduleContent.questions[currentQuestion].options.map((option, index) => (
                    <button
                      key={index}
                      data-testid={`option-${index}`}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showResult}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        showResult
                          ? index === moduleContent.questions[currentQuestion].correct
                            ? 'border-green-500 bg-green-500/20'
                            : selectedAnswer === index
                              ? 'border-red-500 bg-red-500/20'
                              : 'border-slate-200 bg-slate-100/50'
                          : selectedAnswer === index
                            ? 'border-violet-500 bg-violet-500/20'
                            : 'border-slate-200 bg-slate-100/50 hover:border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          showResult
                            ? index === moduleContent.questions[currentQuestion].correct
                              ? 'bg-green-500 text-white'
                              : selectedAnswer === index
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-100 text-slate-500'
                            : selectedAnswer === index
                              ? 'bg-violet-500 text-white'
                              : 'bg-slate-100 text-slate-500'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className={showResult 
                          ? index === moduleContent.questions[currentQuestion].correct
                            ? 'text-green-600'
                            : selectedAnswer === index
                              ? 'text-red-600'
                              : 'text-slate-500'
                          : 'text-slate-700'
                        }>
                          {option}
                        </span>
                        {showResult && index === moduleContent.questions[currentQuestion].correct && (
                          <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto" />
                        )}
                        {showResult && selectedAnswer === index && index !== moduleContent.questions[currentQuestion].correct && (
                          <XCircle className="w-5 h-5 text-red-600 ml-auto" />
                        )}
                      </div>
                    </button>
                  ))}

                  {/* Explanation */}
                  {showResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30"
                    >
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-blue-600 font-medium">Explanation</p>
                          <p className="text-slate-600 text-sm">
                            {moduleContent.questions[currentQuestion].explanation}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div className="pt-4 flex gap-3">
                    {!showResult ? (
                      <Button
                        data-testid="submit-answer-btn"
                        onClick={handleSubmitAnswer}
                        disabled={selectedAnswer === null}
                        className={`flex-1 bg-gradient-to-r ${subjectConfig.color}`}
                      >
                        Check Answer
                      </Button>
                    ) : (
                      <Button
                        data-testid="next-question-btn"
                        onClick={handleNextQuestion}
                        className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600"
                      >
                        {currentQuestion < 2 ? 'Next Question' : 'See Results'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            // Quiz Complete - Results
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-gradient-to-br from-violet-900/30 to-indigo-900/30 border-violet-500/30">
                <CardContent className="p-8 text-center">
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
                    score === 3 ? 'bg-green-500/20' : score >= 2 ? 'bg-amber-500/20' : 'bg-orange-500/20'
                  }`}>
                    {score === 3 ? (
                      <Trophy className="w-10 h-10 text-amber-600" />
                    ) : score >= 2 ? (
                      <Award className="w-10 h-10 text-green-600" />
                    ) : (
                      <Star className="w-10 h-10 text-orange-600" />
                    )}
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {score === 3 ? 'Perfect Score!' : score >= 2 ? 'Great Job!' : 'Keep Practicing!'}
                  </h2>
                  
                  <p className="text-slate-600 mb-6">
                    You got <span className="text-violet-600 font-bold">{score}</span> out of 3 questions correct.
                  </p>

                  {/* Score indicator */}
                  <div className="flex justify-center gap-2 mb-8">
                    {answers.map((answer, i) => (
                      <div
                        key={i}
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          answer.correct ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}
                      >
                        {answer.correct 
                          ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                          : <XCircle className="w-5 h-5 text-red-600" />
                        }
                      </div>
                    ))}
                  </div>

                  {score >= 2 ? (
                    <Button
                      data-testid="complete-module-btn"
                      onClick={handleCompleteModule}
                      className="bg-gradient-to-r from-green-600 to-emerald-600"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Complete Module
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-slate-500 text-sm">
                        You need at least 2 correct answers to complete this module.
                      </p>
                      <Button
                        onClick={() => {
                          setShowQuiz(false);
                          setCurrentSection(0);
                          setCurrentQuestion(0);
                          setSelectedAnswer(null);
                          setAnswers([]);
                          setShowResult(false);
                          setQuizComplete(false);
                          setScore(0);
                        }}
                        className="bg-gradient-to-r from-violet-600 to-indigo-600"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LearningModule;
